import { Checkbox, Input, InputNumber, Modal, Typography } from 'antd';
import { useEffect, useState } from 'react';

export type MenuItem = {
  id: number;
  name: string;
  price: number;
  imageUrl?: string;
  isDrink?: boolean;
  so_luong_san_pham_tren_don_vi?: number;
  so_luong_mac_dinh_hien_thi?: number;
  mac_dinh_them_vao_ban?: boolean;
};

export type SelectedMenuItem = {
  menuId: number;
  quantity: number;
  ghi_chu?: string;
};

export const menuItems: MenuItem[] = [
  { id: 1, name: 'Cà phê sữa', price: 25000 },
  { id: 2, name: 'Bún chả', price: 65000 },
  { id: 3, name: 'Phở bò', price: 70000 },
  { id: 4, name: 'Trà đá', price: 15000 },
  { id: 5, name: 'Gà rán', price: 85000 },
  { id: 6, name: 'Nước ngọt', price: 20000 },
];

type ThemMonModalProps = {
  open: boolean;
  selectedTableName?: string;
  confirmLoading?: boolean;
  menuItems?: MenuItem[];
  currentOrders?: Array<{ id: number; quantity: number }>;
  selectedItems: SelectedMenuItem[];
  onChange: (items: SelectedMenuItem[]) => void;
  onConfirm: () => void;
  onCancel: () => void;
};

const formatMoney = (value: number) =>
  new Intl.NumberFormat('vi-VN', { style: 'currency', currency: 'VND' }).format(value);

const getDefaultQuantity = (item: MenuItem) =>
  typeof item.so_luong_mac_dinh_hien_thi === 'number' &&
  Number.isInteger(item.so_luong_mac_dinh_hien_thi) &&
  item.so_luong_mac_dinh_hien_thi > 0
    ? item.so_luong_mac_dinh_hien_thi
    : 1;

const normalizeVietnameseText = (value: string) =>
  value
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/đ/g, 'd');

export default function ThemMonModal({
  open,
  selectedTableName,
  confirmLoading = false,
  menuItems: items = menuItems,
  currentOrders = [],
  selectedItems,
  onChange,
  onConfirm,
  onCancel,
}: ThemMonModalProps) {
  const { Text } = Typography;
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    if (open) setSearchTerm('');
  }, [open]);

  const normalizedSearchTerm = normalizeVietnameseText(searchTerm.trim());
  const filteredItems = items.filter((item) =>
    normalizeVietnameseText(item.name).includes(normalizedSearchTerm)
  );

  const updateItem = (menuId: number, quantity: number | null) => {
    onChange(
      selectedItems.map((item) =>
        item.menuId === menuId ? { ...item, quantity: Math.max(1, quantity ?? 1) } : item
      )
    );
  };

  const updateItemNote = (menuId: number, ghi_chu: string) => {
    onChange(
      selectedItems.map((item) =>
        item.menuId === menuId ? { ...item, ghi_chu } : item
      )
    );
  };

  const toggleItem = (item: MenuItem, checked: boolean) => {
    onChange(
      checked
        ? [...selectedItems, { menuId: item.id, quantity: getDefaultQuantity(item), ghi_chu: '' }]
        : selectedItems.filter((selected) => selected.menuId !== item.id)
    );
  };

  return (
    <>
      <style>{`
        .menu-item-image {
          transition: transform 0.2s ease, box-shadow 0.2s ease;
          cursor: zoom-in;
          display: block;
        }

        .menu-item-image:hover {
          transform: scale(2.4);
          box-shadow: 0 10px 24px rgba(0, 0, 0, 0.2);
          z-index: 10;
          position: relative;
        }
      `}</style>

      <Modal
        className="add-dish-modal"
        title={`Thêm món cho ${selectedTableName || 'bàn'}`}
        open={open}
        onOk={onConfirm}
        onCancel={onCancel}
        confirmLoading={confirmLoading}
        okText="Xác nhận"
        cancelText="Hủy"
        okButtonProps={{ disabled: selectedItems.length === 0 }}
      >
        <div className="add-dish-modal-content">
          <Input
            allowClear
            placeholder="Tìm theo tên món..."
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
          />
          <Text strong>Chọn món và số lượng</Text>
          <div className="add-dish-list">
            {filteredItems.map((item) => {
              const selectedItem = selectedItems.find((selected) => selected.menuId === item.id);
              const currentQuantity = currentOrders.find((order) => order.id === item.id)?.quantity ?? 0;

              return (
                <div key={item.id} style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <div
                    style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}
                  >
                    <div
                      style={{ display: 'flex', alignItems: 'center', gap: 12 }}
                    >
                      {item.imageUrl ? (
                        <img
                          src={item.imageUrl}
                          alt={item.name}
                          className="menu-item-image"
                          style={{ width: 42, height: 42, objectFit: 'cover', borderRadius: 8 }}
                        />
                      ) : (
                        <div
                          className="menu-item-image"
                          style={{
                            width: 42,
                            height: 42,
                            borderRadius: 8,
                            background: '#f0f0f0',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: 10,
                            color: '#999',
                          }}
                        >
                          IMG
                        </div>
                      )}
                      <div>
                        <Checkbox
                          checked={Boolean(selectedItem)}
                          onChange={(event) => toggleItem(item, event.target.checked)}
                        >
                          {item.name} - {formatMoney(item.price)}
                        </Checkbox>
                        <div style={{ marginLeft: 24 }}>
                          <Text type="secondary">Hiện có: {currentQuantity}</Text>
                        </div>
                      </div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                      <InputNumber
                        aria-label={`Số lượng ${item.name}`}
                        min={1}
                        value={selectedItem?.quantity ?? getDefaultQuantity(item)}
                        disabled={!selectedItem}
                        onChange={(value) => updateItem(item.id, value)}
                        style={{ width: 76 }}
                      />
                    </div>
                  </div>

                  {selectedItem && (
                    <div style={{ width: '100%', paddingLeft: 54, boxSizing: 'border-box' }}>
                      <Input
                        aria-label={`Ghi chú ${item.name}`}
                        placeholder={`Ghi chú cho ${item.name}`}
                        value={selectedItem.ghi_chu ?? ''}
                        onChange={(event) => updateItemNote(item.id, event.target.value)}
                        allowClear
                        style={{ width: '100%' }}
                      />
                    </div>
                  )}
                </div>
              );
            })}
            {filteredItems.length === 0 && <Text type="secondary">Không tìm thấy món phù hợp</Text>}
          </div>
          <Text type="secondary">Đã chọn {selectedItems.length} món</Text>
        </div>
      </Modal>
    </>
  );
}
